import React from 'react';
import { Link } from 'react-router-dom';
import { Package, Clock, Shield, Heart, Truck, CheckCircle } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const Spedizioni = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 text-sm text-gray-500 mb-6">
          <Link to="/" className="hover:text-purple-600">Home</Link>
          <span>›</span>
          <span className="text-gray-900">Spedizioni</span>
        </div>

        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Spedizioni</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            La cura che dedichiamo ai nostri liquori si estende anche alla consegna. 
            Ogni ordine viene seguito con attenzione dal nostro laboratorio fino alla tua porta.
          </p>
        </div>

        {/* Video Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Scopri il nostro processo di spedizione
            </h2>
            <p className="text-gray-600">
              Guarda nel video l'intero processo: dalla preparazione del tuo amaro, all'imballaggio, 
              fino alla partenza dal nostro laboratorio di Crotone.
            </p>
          </div>
          
          {/* YouTube Video Embed */}
          <div className="relative w-full" style={{ paddingBottom: '56.25%' /* 16:9 aspect ratio */ }}>
            <iframe
              className="absolute top-0 left-0 w-full h-full rounded-lg"
              src="https://www.youtube.com/embed/F_ePa8AZuME"
              title="Il processo di spedizione di IlTuoAmaro.it"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-12">
          
          {/* La cura parte dal tuo progetto */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <Heart className="w-8 h-8 text-purple-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">La cura parte dal tuo progetto</h2>
                <p className="text-gray-700 leading-relaxed">
                  Ogni ordine su IlTuoAmaro.it viene seguito con la stessa attenzione che dedichiamo alla creazione dei nostri liquori. 
                  Le bottiglie personalizzate vengono confezionate a mano in imballaggi rinforzati, progettati appositamente per 
                  proteggere il vetro e le etichette durante il trasporto. Così, anche dopo un lungo viaggio, il tuo liquore arriva 
                  integro e perfettamente presentato.
                </p>
              </div>
            </div>
          </div>

          <hr className="border-gray-200" />

          {/* Spedizioni rapide */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <Truck className="w-8 h-8 text-purple-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Spedizioni rapide in tutta Italia</h2>
                <p className="text-gray-700 leading-relaxed">
                  Collaboriamo con corrieri affidabili e veloci, per garantire consegne sicure e puntuali. In genere, gli ordini 
                  vengono evasi in 24/48 ore lavorative dal momento della produzione e dell'approvazione grafica, così che il tuo 
                  progetto prenda vita nel più breve tempo possibile.
                </p>
              </div>
            </div>
          </div>

          <hr className="border-gray-200" />

          {/* Costi di Spedizione */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <Package className="w-8 h-8 text-purple-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Costi di Spedizione</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  I nostri costi di spedizione sono strutturati per garantire la massima convenienza per ordini di maggiori quantità.
                </p>
                
                <div className="bg-gradient-to-r from-green-50 to-green-100 border-l-4 border-green-500 p-4 rounded-lg mb-6">
                  <p className="text-lg font-bold text-green-800 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Spedizione GRATUITA da 120 bottiglie in su!
                  </p>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full bg-gray-50 rounded-lg overflow-hidden">
                    <thead className="bg-purple-600 text-white">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-semibold">Quantità Bottiglie</th>
                        <th className="px-6 py-3 text-left text-sm font-semibold">Costo Spedizione</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr className="hover:bg-gray-100">
                        <td className="px-6 py-4 text-gray-900">24 bottiglie</td>
                        <td className="px-6 py-4 text-gray-900 font-semibold">15,00€ + IVA</td>
                      </tr>
                      <tr className="hover:bg-gray-100">
                        <td className="px-6 py-4 text-gray-900">30 bottiglie (24 + 6)</td>
                        <td className="px-6 py-4 text-gray-900 font-semibold">20,00€ + IVA</td>
                      </tr>
                      <tr className="hover:bg-gray-100">
                        <td className="px-6 py-4 text-gray-900">36 bottiglie (24 + 12)</td>
                        <td className="px-6 py-4 text-gray-900 font-semibold">25,00€ + IVA</td>
                      </tr>
                      <tr className="hover:bg-gray-100">
                        <td className="px-6 py-4 text-gray-900">42 bottiglie (24 + 18)</td>
                        <td className="px-6 py-4 text-gray-900 font-semibold">30,00€ + IVA</td>
                      </tr>
                      <tr className="hover:bg-gray-100">
                        <td className="px-6 py-4 text-gray-900">48 bottiglie (24 + 24)</td>
                        <td className="px-6 py-4 text-gray-900 font-semibold">35,00€ + IVA</td>
                      </tr>
                      <tr className="hover:bg-gray-100">
                        <td className="px-6 py-4 text-gray-900">54 bottiglie (24 + 30)</td>
                        <td className="px-6 py-4 text-gray-900 font-semibold">40,00€ + IVA</td>
                      </tr>
                      <tr className="hover:bg-gray-100">
                        <td className="px-6 py-4 text-gray-900">60 bottiglie (24 + 36)</td>
                        <td className="px-6 py-4 text-gray-900 font-semibold">45,00€ + IVA</td>
                      </tr>
                      <tr className="hover:bg-gray-100">
                        <td className="px-6 py-4 text-gray-900">66 bottiglie (24 + 42)</td>
                        <td className="px-6 py-4 text-gray-900 font-semibold">50,00€ + IVA</td>
                      </tr>
                      <tr className="hover:bg-gray-100">
                        <td className="px-6 py-4 text-gray-900">72 bottiglie (24 + 48)</td>
                        <td className="px-6 py-4 text-gray-900 font-semibold">55,00€ + IVA</td>
                      </tr>
                      <tr className="hover:bg-gray-100">
                        <td className="px-6 py-4 text-gray-900">78 bottiglie (24 + 54)</td>
                        <td className="px-6 py-4 text-gray-900 font-semibold">60,00€ + IVA</td>
                      </tr>
                      <tr className="hover:bg-gray-100">
                        <td className="px-6 py-4 text-gray-900">84 bottiglie (24 + 60)</td>
                        <td className="px-6 py-4 text-gray-900 font-semibold">65,00€ + IVA</td>
                      </tr>
                      <tr className="hover:bg-gray-100">
                        <td className="px-6 py-4 text-gray-900">90 bottiglie (24 + 66)</td>
                        <td className="px-6 py-4 text-gray-900 font-semibold">70,00€ + IVA</td>
                      </tr>
                      <tr className="hover:bg-gray-100">
                        <td className="px-6 py-4 text-gray-900">96 bottiglie (24 + 72)</td>
                        <td className="px-6 py-4 text-gray-900 font-semibold">75,00€ + IVA</td>
                      </tr>
                      <tr className="hover:bg-gray-100">
                        <td className="px-6 py-4 text-gray-900">102 bottiglie (24 + 78)</td>
                        <td className="px-6 py-4 text-gray-900 font-semibold">80,00€ + IVA</td>
                      </tr>
                      <tr className="hover:bg-gray-100">
                        <td className="px-6 py-4 text-gray-900">108 bottiglie (24 + 84)</td>
                        <td className="px-6 py-4 text-gray-900 font-semibold">85,00€ + IVA</td>
                      </tr>
                      <tr className="hover:bg-gray-100">
                        <td className="px-6 py-4 text-gray-900">114 bottiglie (24 + 90)</td>
                        <td className="px-6 py-4 text-gray-900 font-semibold">90,00€ + IVA</td>
                      </tr>
                      <tr className="bg-green-50 hover:bg-green-100">
                        <td className="px-6 py-4 text-green-800 font-bold">120+ bottiglie</td>
                        <td className="px-6 py-4 text-green-800 font-bold">GRATUITA 🎉</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Nota:</strong> Il costo di spedizione parte da 15,00€ + IVA per 24 bottiglie, 
                    e aumenta di 5,00€ + IVA per ogni multiplo di 6 bottiglie aggiuntive.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <hr className="border-gray-200" />

          {/* Sicurezza e garanzia */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <Shield className="w-8 h-8 text-purple-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Sicurezza e garanzia</h2>
                <p className="text-gray-700 leading-relaxed">
                  Ogni pacco è tracciabile e monitorato in tempo reale. E se dovesse verificarsi un danno durante la spedizione, 
                  provvediamo immediatamente alla sostituzione, senza costi aggiuntivi. La tua soddisfazione è parte integrante 
                  del nostro servizio.
                </p>
              </div>
            </div>
          </div>

          <hr className="border-gray-200" />

          {/* Un'esperienza che inizia dall'apertura */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <Package className="w-8 h-8 text-purple-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Un'esperienza che inizia dall'apertura</h2>
                <p className="text-gray-700 leading-relaxed">
                  Ricevere il tuo liquore personalizzato è già un momento speciale. Ogni confezione è preparata con cura, 
                  accompagnata da omaggi, campioni e piccoli dettagli che raccontano la passione e l'artigianalità del 
                  Liquorificio Lacinio.
                </p>
              </div>
            </div>
          </div>

          {/* Process Summary */}
          <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-lg p-8 text-white">
            <h2 className="text-2xl font-bold mb-6 text-center">Il nostro processo di spedizione</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="flex justify-center mb-3">
                  <CheckCircle className="w-8 h-8" />
                </div>
                <h3 className="font-semibold mb-2">1. Produzione</h3>
                <p className="text-purple-100 text-sm">
                  Il tuo liquore viene preparato con ingredienti selezionati nel nostro laboratorio di Crotone.
                </p>
              </div>
              <div className="text-center">
                <div className="flex justify-center mb-3">
                  <Package className="w-8 h-8" />
                </div>
                <h3 className="font-semibold mb-2">2. Confezionamento</h3>
                <p className="text-purple-100 text-sm">
                  Ogni bottiglia viene confezionata a mano con imballaggi rinforzati e protettivi.
                </p>
              </div>
              <div className="text-center">
                <div className="flex justify-center mb-3">
                  <Truck className="w-8 h-8" />
                </div>
                <h3 className="font-semibold mb-2">3. Spedizione</h3>
                <p className="text-purple-100 text-sm">
                  Il pacco parte con corrieri affidabili e viene tracciato fino alla consegna.
                </p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Pronto a creare il tuo liquore?</h2>
            <p className="text-gray-600 mb-6">
              Inizia subito la personalizzazione del tuo amaro e scopri la cura che dedichiamo ad ogni ordine.
            </p>
            <Link
              to="/builder"
              className="inline-flex items-center px-8 py-4 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors shadow-lg hover:shadow-xl"
            >
              <Package className="w-5 h-5 mr-2" />
              Inizia la Creazione
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Spedizioni;